import { useState, useMemo, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import { useCommonCodes } from '../context/CommonCodesContext'
import { getSiteStatusBadge, SITE_STATUS_MAP } from '../constants/siteStatus'
import type { SiteInfo } from '../views/CalendarMultiView'
import { formatISO } from '../views/scheduleUtils'
import { useScheduleRequestCount } from '../hooks/useScheduleRequestCount'
import { buildScheduleReplaceNotice } from '../constants/scheduleReplaceNotice.logic'
import {
  SiteRegistrationPopup,
  AssigneeBadges, AssigneeSelect,
  type CompanyMember,
} from '@indipia/shared-features'
import {
  Button, Badge, FilterChip, Modal,
  DateRangePicker, type DateRangeValue, // §15.14 — 기간 선택(알약 트리거 + 2개월 팝오버)
  SidePanel,                       // §15.44 — 접이식 보조 패널(폭 308 · 28px 이중 셰브런 토글)
  SearchCombo,                     // §15.13 — 검색 조합(입력 + "검색" 버튼)
  EmptyState, ErrorState, Skeleton, // §15.37 · §15.30 — 상태 UX(D-07 §17.1 Loading/Empty/Error)
} from '@indipia/ui-core'
import type { NavFn, View } from '../pages/SchedulerPage'

// ── 타입 / 상수 (RightPanel 전용) ───────────────────────────────────────────

type SchedStatusFilter = 'all' | 'ready' | 'in_progress' | 'done'
// 라벨은 공통코드(E01_SCHEDULE_STATUS)에서 가져온다 — RightPanel 내부에서 useCommonCodes()로 조합
const SCHED_STATUS_FILTER_CODES: Array<{ code: string | null; filter: SchedStatusFilter }> = [
  { code: null,          filter: 'all' },
  { code: 'READY',       filter: 'ready' },
  { code: 'IN_PROGRESS', filter: 'in_progress' },
  { code: 'DONE',        filter: 'done' },
]

// 인디플래너 홈(캘린더)의 우측 현장 패널 전용 정렬 순서 — 시공중 → 준비중
// (시공완료는 panelSites 단계에서 이미 제외됨)
export const CAL_HOME_STATUS_ORDER: Record<string, number> = { IN_PROGRESS: 0, READY: 1 }

// 스케줄 관리(스케줄/캘린더/리스트)의 우측 현장 패널 정렬 순서 — 시공중 → 준비중 → 지연
// (시공완료는 panelSites 단계에서 이미 제외됨)
export const SCHED_PANEL_STATUS_ORDER: Record<string, number> = { IN_PROGRESS: 0, READY: 1, DELAYED: 2 }

// 전송및공유이력의 우측 현장 패널 정렬 순서 — 시공중 → 준비중 → 지연 → 시공완료
// (이 화면은 시공완료 현장도 함께 노출한다)
export const HISTORY_PANEL_STATUS_ORDER: Record<string, number> = { IN_PROGRESS: 0, READY: 1, DELAYED: 2, DONE: 3, COMPLETED: 3 }

// "스케줄 관리"(스케줄/캘린더/리스트) 소속 view 집합 —
// 이 그룹 밖에서 안으로 들어올 때 활성 현장(schedActiveSiteId)을 리셋할지 판단하는 기준.
export const SCHED_MGMT_VIEWS: ReadonlySet<View> = new Set(['gantt', 'cal-b2', 'work-type'])

// toISOString 은 UTC 날짜라 KST 00~09시에 하루 밀린다 — formatISO 는 로컬 기준이다
export function isoToday(): string { return formatISO(new Date()) }
export function isoMonthAgo(): string { return formatISO(monthsAgo(1)) }
/** 'YYYY-MM-DD' → 로컬 자정 Date. new Date(문자열)은 UTC 자정으로 파싱돼 시간대에 따라 하루 밀린다 */
function parseISO(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d)
}
/** 최근 N개월의 시작일 — 종료일은 늘 오늘이다 */
function monthsAgo(months: number): Date {
  const d = new Date()
  d.setMonth(d.getMonth() - months)
  return d
}

// 날짜 필터 기간 단축 pills — "최근 N개월"(종료 = 오늘). work-log datePreset.ts 와 같은 기준.
// `직접 설정` 칩은 없다 — 달력에서 고르는 행위 자체가 직접설정이고, DateRangePicker 의 active 가 그 상태를 표시한다.
type MonthMode = 1 | 3 | 6 | 12
const DATE_MODES: Array<{ label: string; mode: MonthMode }> = [
  { label: '1개월', mode: 1 },
  { label: '3개월', mode: 3 },
  { label: '6개월', mode: 6 },
  { label: '1년',   mode: 12 },
]


// summary 를 배열로 주면 항목마다 배지 하나 — 담당자·관리자 섹션은 접힌 상태에서 선택 직원 이름을 배지로 모두 표시(v1.9)
function RPSectionToggle({ label, open, onToggle, summary }: {
  label: string; open: boolean; onToggle: () => void; summary?: string | string[]
}) {
  const summaries = summary == null ? [] : Array.isArray(summary) ? summary : [summary]
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full shrink-0 items-center justify-between gap-03 border-b border-border px-04 py-03 text-left transition-colors hover:bg-surface"
    >
      <span className="flex min-w-0 flex-wrap items-center gap-02">
        <span className="shrink-0 text-[length:var(--type-caption-size)] font-semibold text-text-secondary">{label}</span>
        {summaries.map(s => (
          <span key={s} className="rounded-full bg-group px-03 text-caption font-semibold text-group-onPrimary">{s}</span>
        ))}
      </span>
      <svg
        width="11" height="11" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        className={`shrink-0 text-text-muted transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  )
}

// ── 우측 고객·현장 패널 ─────────────────────────────────────────────────────
export function RightPanel({
  sites = [],
  inactiveSiteIds,
  onToggle,
  emptyMessage = '현장이 없습니다',
  loading = false,
  loadError = false,
  onRetry,
  navigate,
  statusOrder,
  pinActiveFirst = true,
  dateFilter,
  onSiteChanged,
  showCreateCta = false,
  noSchedules = false,
  collapseWhenNoSchedules = false,
  assignee,
  hideDoneChip = false,
}: {
  sites?: SiteInfo[]
  inactiveSiteIds: Set<number>
  onToggle: (scheduleId: number) => void
  emptyMessage?: string
  // 최초 조회 중 — 빈 목록 UI가 깜빡이지 않도록 Skeleton 을 먼저 그린다(D-07 §17.1)
  loading?: boolean
  // 목록이 0건일 때 "진짜 빈 상태"인지 "조회 실패"인지 구분해서 보여주기 위함
  loadError?: boolean
  onRetry?: () => void
  navigate: NavFn
  // 상태값별 정렬 순서 override — 지정하지 않으면 준비중→시공중→시공완료 기본값 사용
  statusOrder?: Record<string, number>
  // 선택된(활성) 현장을 목록 맨 위로 고정할지 여부 — 캘린더 홈에서는 "해당 달" 기준으로
  // 이미 걸러온 목록의 상태값 순서를 그대로 따라야 하므로 false로 넘긴다.
  pinActiveFirst?: boolean
  // 지정 시 "날짜" 필터 섹션 노출(전송및공유이력 전용) — 조회 기간 직접 편집
  dateFilter?: { from: string; to: string; onChange: (from: string, to: string) => void }
  // 시공완료 처리·삭제 성공 후 부모의 현장 목록(allSites)을 다시 불러오도록 알림
  onSiteChanged?: () => void
  // 스케줄/캘린더/리스트 전용 — sites가 완전히 0건일 때 C-0 화면기획서 스타일의
  // "공사스케줄생성" 유도 UI를 보여준다(검색·필터로 인한 0건일 때는 기존 안내 문구 유지)
  showCreateCta?: boolean
  // 회사에 스케줄이 하나도 없는지 — C-0 유도 화면 판단용(담당자 조건으로 걸러진 목록과 무관)
  noSchedules?: boolean
  // 간트에서 0건 판정이 확정되면 패널을 닫은 채 시작한다(F-12) — 한 번만 닫고 이후 사용자가 연 상태는 존중
  collapseWhenNoSchedules?: boolean
  // 담당자·관리자 조건(v1.9) — 값은 부모가 들고 서버 조회(S-1 memberIds)에 쓴다. 이 패널은 표시·변경만.
  assignee?: { values: number[]; onChange: (ids: number[]) => void; members: CompanyMember[]; myId: number | null }
  // 상태 칩의 '시공완료'를 숨긴다(F-18) — 시공완료가 목록에서 항상 제외되는 화면(스케줄·캘린더·리스트)에서 켠다. 이력 화면은 끈다.
  hideDoneChip?: boolean
}) {
  const user = useAuthStore(s => s.user)
  const { siteStatuses } = useCommonCodes()
  const statusLabel = (code: string) => siteStatuses.find(c => c.code === code)?.codeName || SITE_STATUS_MAP[code]?.label || code
  const schedStatusTabs = useMemo(() => SCHED_STATUS_FILTER_CODES
    .filter(({ filter }) => !(hideDoneChip && filter === 'done'))
    .map(({ code, filter }) => ({
      filter, label: code === null ? '전체' : statusLabel(code),
    })), [siteStatuses, hideDoneChip])
  const [open,     setOpen]     = useState(true)
  const noScheduleCollapsedRef = useRef(false)
  useEffect(() => {
    if (!collapseWhenNoSchedules || !noSchedules || noScheduleCollapsedRef.current) return
    noScheduleCollapsedRef.current = true
    setOpen(false)
  }, [collapseWhenNoSchedules, noSchedules])
  const [siteOpen, setSiteOpen] = useState(false)

  // 시공완료 처리 · 삭제 (현장 목록 페이지 SiteListView.tsx의 액션 버튼과 동일 패턴)
  const [completingId,      setCompletingId]      = useState<number | null>(null)
  const [deletingId,        setDeletingId]        = useState<number | null>(null)
  const [confirmCompleteSite, setConfirmCompleteSite] = useState<SiteInfo | null>(null)
  const [confirmDeleteSite,   setConfirmDeleteSite]   = useState<SiteInfo | null>(null)
  const deleteNotice = buildScheduleReplaceNotice({ mode: 'delete', requestCount: useScheduleRequestCount(confirmDeleteSite?.scheduleId) })

  const handleConfirmComplete = async () => {
    if (!confirmCompleteSite) return
    const site = confirmCompleteSite
    setConfirmCompleteSite(null)
    setCompletingId(site.scheduleId)
    try {
      const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
      await apiClient.post(`/schedule/schedules/${site.scheduleId}/complete`, {}, { headers })
      onSiteChanged?.()
    } catch {
      toast.error('상태 변경에 실패했습니다.')
    } finally {
      setCompletingId(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDeleteSite) return
    const site = confirmDeleteSite
    setConfirmDeleteSite(null)
    setDeletingId(site.scheduleId)
    try {
      const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
      await apiClient.delete(`/schedule/schedules/${site.scheduleId}`, { headers })
      onSiteChanged?.()
    } catch {
      toast.error('삭제에 실패했습니다.')
    } finally {
      setDeletingId(null)
    }
  }

  // 검색
  const [search, setSearch] = useState('')

  // 날짜 필터 (전송및공유이력 전용)
  const [dateOpen, setDateOpen] = useState(false)
  // null = 직접 설정(달력에서 고른 기간). 기본 조회 기간이 최근 1개월이라 진입 시 1개월 칩이 켜져 있는 게 맞다.
  const [dateMode, setDateMode] = useState<MonthMode | null>(1)
  // 피커 값을 로컬로 들고 있는 이유 — DateRangePicker 는 첫 클릭에 {start, end:null}, 두 번째 클릭에 완성 범위를 준다.
  // 중간 상태를 버리면 value.start 가 옛 시작일에 머물러 두 번째 클릭이 엉뚱한 범위를 만든다.
  const [pickerRange, setPickerRange] = useState<DateRangeValue>(() => ({
    start: dateFilter ? parseISO(dateFilter.from) : null,
    end:   dateFilter ? parseISO(dateFilter.to)   : null,
  }))

  // 프리셋 칩 — 모드만 바꾸지 않고 기간을 실제로 적용한다
  const applyMonths = (mode: MonthMode) => {
    if (!dateFilter) return
    const start = monthsAgo(mode)
    const end = new Date()
    setDateMode(mode)
    setPickerRange({ start, end })
    dateFilter.onChange(formatISO(start), formatISO(end))
  }

  const handleRangePick = (v: DateRangeValue) => {
    setPickerRange(v)
    if (!dateFilter || !v.start || !v.end) return
    setDateMode(null)
    dateFilter.onChange(formatISO(v.start), formatISO(v.end))
  }

  // 상태 필터
  const [statusOpen,   setStatusOpen]   = useState(false)
  const [statusFilter, setStatusFilter] = useState<SchedStatusFilter>('all')
  // 이력 화면에서 '시공완료'를 골라 둔 채 칩이 없는 화면으로 오면 0건이 되므로 '전체'로 되돌린다
  useEffect(() => { if (hideDoneChip && statusFilter === 'done') setStatusFilter('all') }, [hideDoneChip, statusFilter])

  const STATUS_ORDER: Record<string, number> = statusOrder
    ?? { READY: 0, IN_PROGRESS: 1, DONE: 2, COMPLETED: 2, DELAYED: 3 }

  // 활성/비활성 → 상태 → 공사 시작일 순 — sites가 바뀔 때(API 최초 수신)만 정렬,
  // inactiveSiteIds 토글 시에는 순서 고정(카드가 튀지 않도록).
  // pinActiveFirst가 false인 경우(인디플래너 홈)는 이미 상위에서 "해당 달" 기준으로
  // sites를 걸러왔으므로, 캘린더 내부의 active/inactive 구분(주/월 뷰별 표시 여부)을
  // 정렬에 반영하지 않고 상태값 순서를 그대로 따른다.
  const sortedSites = useMemo(() => [...sites].sort((a, b) => {
    if (pinActiveFirst) {
      const aInactive = inactiveSiteIds.has(a.scheduleId) ? 1 : 0
      const bInactive = inactiveSiteIds.has(b.scheduleId) ? 1 : 0
      if (aInactive !== bInactive) return aInactive - bInactive
    }
    const aOrder = STATUS_ORDER[(a.scheduleStatus ?? '').toUpperCase()] ?? 4
    const bOrder = STATUS_ORDER[(b.scheduleStatus ?? '').toUpperCase()] ?? 4
    if (aOrder !== bOrder) return aOrder - bOrder
    return (b.startDate ?? '').localeCompare(a.startDate ?? '')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sites, pinActiveFirst])

  const filteredSites = sortedSites.filter(s => {
    if (search) {
      const q = search.toLowerCase()
      if (!s.siteName.toLowerCase().includes(q) && !s.customerName.toLowerCase().includes(q)) return false
    }
    if (statusFilter !== 'all') {
      const st = (s.scheduleStatus ?? '').toUpperCase()
      if (statusFilter === 'ready'       && st !== 'READY') return false
      if (statusFilter === 'in_progress' && st !== 'IN_PROGRESS') return false
      if (statusFilter === 'done'        && st !== 'DONE' && st !== 'COMPLETED') return false
    }
    return true
  })

  const statusSummary = statusFilter !== 'all'
    ? schedStatusTabs.find(t => t.filter === statusFilter)?.label
    : undefined

  // 담당자·관리자 섹션 — 접힌 상태에서는 선택 직원 이름을 배지로 모두 표시
  const [memberOpen, setMemberOpen] = useState(false)
  const memberFilterIds = assignee?.values ?? []
  const memberSummary = memberFilterIds.map(id =>
    assignee?.members.find(m => m.memberId === id)?.memberName ?? (id === assignee?.myId ? '나' : `회원 ${id}`))

  return (
    <>
      {/* D-09 §15.44 SidePanel — 폭(308)·토글(28 이중 셰브런)·헤더 줄을 컴포넌트가 소유한다.
          .side-panel-body 가 스크롤을 갖고 있으므로, 검색·필터를 고정하고 카드 목록만
          스크롤시키기 위해 본문을 h-full flex-col 로 한 겹 감싼다(내용이 body를 넘지 않아
          이중 스크롤바가 생기지 않는다). */}
      <SidePanel
        open={open}
        onToggle={() => setOpen(o => !o)}
        label="현장 패널"
        title={
          <>
            <span>현장 목록</span>
            <Button
              variant="primary"
              size="md"
              onClick={() => setSiteOpen(true)}
              className="gap-1.5"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              새 현장
            </Button>
          </>
        }
      >
        <div className="flex h-full flex-col">

          {/* 검색 — §15.13 SearchCombo. onChange 는 실시간 필터, onSearch(버튼·Enter)는 확정 */}
          <div className="shrink-0 border-b border-border px-2.5 py-2">
            <SearchCombo
              value={search}
              onChange={setSearch}
              onSearch={setSearch}
              size="sm"
              placeholder="현장명, 고객명"
            />
          </div>

          {/* 날짜 필터 (전송및공유이력 전용) */}
          {dateFilter && (
            <>
              <RPSectionToggle
                label="날짜"
                open={dateOpen}
                onToggle={() => setDateOpen(v => !v)}
                summary={dateMode !== null ? DATE_MODES.find(m => m.mode === dateMode)?.label : `${dateFilter.from} ~ ${dateFilter.to}`}
              />
              {dateOpen && (
                <div className="flex shrink-0 flex-col gap-2 border-b border-border bg-surface px-2.5 py-2">
                  {/* 기간 직접 설정 — §15.14 공용 기간 선택기. 옆의 프리셋 칩보다 우선하며
                      어느 쪽이 이겼는지는 active 가 표시한다(work-log 목록 화면들과 같은 배치) */}
                  <DateRangePicker
                    value={pickerRange}
                    onChange={handleRangePick}
                    size="sm"
                    presets={false}
                    active={dateMode === null}
                    className="w-full"
                    aria-label="기간 직접 설정"
                  />
                  {/* 기간 단축 pills */}
                  <div className="flex gap-1 overflow-x-auto">
                    {DATE_MODES.map(({ label, mode }) => (
                      <FilterChip
                        key={mode}
                        active={dateMode === mode}
                        onClick={() => applyMonths(mode)}
                        className="shrink-0"
                      >
                        {label}
                      </FilterChip>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* 상태 필터 */}
          <RPSectionToggle label="상태" open={statusOpen} onToggle={() => setStatusOpen(v => !v)} summary={statusSummary} />
          {statusOpen && (
            <div className="flex shrink-0 flex-wrap gap-1 border-b border-border bg-surface px-2.5 py-2">
              {schedStatusTabs.map(({ label, filter }) => (
                <FilterChip
                  key={filter}
                  active={statusFilter === filter}
                  onClick={() => setStatusFilter(filter)}
                  className="shrink-0"
                >
                  {label}
                </FilterChip>
              ))}
            </div>
          )}

          {/* 담당자·관리자 필터 (v1.9) — 상태(전송및공유이력은 날짜 · 상태) 아래 접이식 */}
          {assignee && (
            <>
              <RPSectionToggle
                label="담당자·관리자"
                open={memberOpen}
                onToggle={() => setMemberOpen(v => !v)}
                summary={memberOpen ? undefined : memberSummary}
              />
              {memberOpen && (
                <div className="shrink-0 border-b border-border bg-surface px-04 py-03">
                  <AssigneeSelect
                    members={assignee.members}
                    values={assignee.values}
                    onChange={assignee.onChange}
                    myId={assignee.myId}
                  />
                </div>
              )}
            </>
          )}

          {/* 현장 카드 목록 — D-07 §17.1 상태 전환: Loading → Error / Empty / Content */}
          <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-2.5 py-2">
            {loading ? (
              // 카드 자리를 그대로 모사한다(스피너 단독·"불러오는 중" 텍스트 금지 — §15.37)
              <div aria-busy="true" className="flex flex-col gap-1.5">
                {[0, 1, 2, 3].map(i => <Skeleton key={i} height={74} />)}
              </div>
            ) : loadError ? (
              <ErrorState
                description="현장 목록을 불러올 수 없어요. 잠시 후 다시 시도해주세요."
                onRetry={onRetry}
              />
            ) : filteredSites.length === 0 && showCreateCta && noSchedules && !search && statusFilter === 'all' ? (
              // 빈 상태 액션은 secondary — §15.1 "주 액션=primary"의 명시적 예외(D-07 §14.2 · D-09 §15.37)
              <EmptyState
                title="아직 생성된 공사 스케줄이 없어요"
                description="먼저 만들어보세요"
                action={
                  <Button variant="secondary" className="gap-1.5" onClick={() => navigate('create-site')}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    공사스케줄생성
                  </Button>
                }
              />
            ) : filteredSites.length === 0 ? (
              search || statusFilter !== 'all' || memberFilterIds.length > 0
                ? <EmptyState title="검색 결과가 없습니다" description="다른 검색어나 조건으로 찾아보세요" />
                : <EmptyState title={emptyMessage} />
            ) : filteredSites.map(site => {
              const inactive = inactiveSiteIds.has(site.scheduleId)

              const badge = getSiteStatusBadge(site.scheduleStatus, siteStatuses.find(c => c.code === site.scheduleStatus)?.codeName)

              let ddayText = '—'
              if (site.endDate) {
                const diff = Math.ceil(
                  (new Date(site.endDate).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0))
                  / (1000 * 60 * 60 * 24)
                )
                ddayText = diff > 0 ? `D-${diff}` : diff === 0 ? 'D-Day' : `D+${Math.abs(diff)}`
              }
              const ddayColor = inactive ? 'var(--shell-on-surface-2)'
                : ddayText.startsWith('D+') ? 'var(--shell-on-surface-2)'
                : ddayText === 'D-Day' ? 'var(--gt-primary)'
                : site.color

              return (
                // 카드 전체 클릭은 마우스용. 키보드·보조기기는 현장명 <button>(클릭이 카드로 버블링)으로
                // 토글한다 — 카드에 role="button"을 두면 안의 완료/삭제 버튼과 중첩돼 nested-interactive.
                // 비활성 표시는 opacity 대신 회색 면 — opacity 는 글자 대비를 AA 아래로 떨어뜨린다.
                <div
                  key={site.scheduleId}
                  onClick={() => onToggle(site.scheduleId)}
                  className="flex cursor-pointer rounded-md border border-border bg-surface-raised transition-colors hover:border-group"
                  style={inactive ? { background: 'var(--shell-bg)' } : undefined}
                >
                  {/* 왼쪽 색상 바 */}
                  <div
                    className="w-[5px] shrink-0 self-stretch rounded-tl-md rounded-bl-md"
                    style={{ background: inactive ? 'var(--gray-300)' : site.color }}
                  />

                  {/* 카드 내용 */}
                  <div className="min-w-0 flex-1 px-3 py-2.5">
                    <div className="mb-1 flex items-center justify-between gap-1">
                      <button
                        type="button"
                        aria-pressed={!inactive}
                        className="min-w-0 truncate text-left text-[length:var(--type-caption-size)] font-bold text-text"
                      >
                        {site.siteName || site.customerName}
                      </button>
                      <Badge status={badge.status} className="shrink-0 whitespace-nowrap !text-[10px]">
                        {badge.label}
                      </Badge>
                    </div>

                    <p className="mb-1.5 text-[length:var(--type-caption-size)] leading-snug text-text-secondary">
                      {site.customerName}
                    </p>

                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[10.5px] font-bold" style={{ color: ddayColor }}>
                        {ddayText}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        {site.scheduleStatus !== 'DONE' && site.scheduleStatus !== 'COMPLETED' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={e => { e.stopPropagation(); setConfirmCompleteSite(site) }}
                            disabled={completingId === site.scheduleId}
                            title="시공완료 처리"
                            className="!h-auto !w-auto gap-0 !p-[3px]"
                            style={{
                              borderColor: 'var(--color-success)',
                              background: 'color-mix(in srgb, var(--color-success) 8%, white)',
                              color: 'var(--color-success)',
                            }}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={e => { e.stopPropagation(); setConfirmDeleteSite(site) }}
                          disabled={deletingId === site.scheduleId}
                          title="스케줄 삭제"
                          className="!h-auto !w-auto gap-0 !p-[3px]"
                          style={{
                            borderColor: 'color-mix(in srgb, var(--color-error) 30%, white)',
                            background: 'color-mix(in srgb, var(--color-error) 8%, white)',
                            color: 'var(--status-danger-600)',
                          }}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </Button>
                      </div>
                    </div>

                    {/* 담당자 · 관리자 — 공사기간(D-day) 줄 아래. 넘치면 관리자는 다음 줄, 지정 없으면 '미지정' (v1.9) */}
                    {assignee && (
                      <AssigneeBadges
                        className="mt-03"
                        chargeMembers={site.chargeMembers}
                        managerMembers={site.managerMembers}
                        filterIds={memberFilterIds}
                        myId={assignee.myId}
                        emptyText="미지정"
                        popoverMe
                        wrap
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </SidePanel>

      {/* 오버레이는 SidePanel 바깥에 둔다 — .side-panel-inner 가 overflow:hidden 이고
          패널이 닫히면 children 이 언마운트되기 때문이다(§15.44). */}
      {siteOpen && (
        <SiteRegistrationPopup
          agentId={8}
          onClose={() => setSiteOpen(false)}
          onComplete={() => {
            setSiteOpen(false)
            // 등록 직후 목록을 다시 불러온다 — 없으면 새 현장이 패널에 나타나지 않는다
            onSiteChanged?.()
          }}
        />
      )}

      {/* ── 시공완료 확인 다이얼로그 (SiteListView.tsx와 동일 패턴) ───────── */}
      <Modal
        open={confirmCompleteSite != null}
        onClose={() => setConfirmCompleteSite(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmCompleteSite(null)}>취소</Button>
          <Button
            variant="primary"
            onClick={handleConfirmComplete}
          >시공완료 처리</Button>
        </>}
      >
        {confirmCompleteSite && (
          <>
            <div style={{ margin: 'calc(-1 * var(--spacing-05)) calc(-1 * var(--spacing-05)) 0', background: 'color-mix(in srgb, var(--color-success) 8%, white)', padding: '22px 24px 16px', borderBottom: '1px solid color-mix(in srgb, var(--color-success) 25%, white)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--shell-surface)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-success)', marginBottom: 4 }}>시공완료 처리</div>
                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--color-success)', lineHeight: 1.6 }}>
                  <strong>"{confirmCompleteSite.siteName}"</strong> 현장을 <strong>시공완료</strong> 상태로 변경합니다.<br />
                  완료 처리 후에는 되돌리기 어렵습니다.
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* ── 삭제 확인 다이얼로그 (SiteListView.tsx와 동일 패턴) ──────────── */}
      <Modal
        open={confirmDeleteSite != null}
        onClose={() => setConfirmDeleteSite(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmDeleteSite(null)}>취소</Button>
          <Button variant="primary" onClick={handleConfirmDelete}>삭제</Button>
        </>}
      >
        {confirmDeleteSite && (
          <>
            <div style={{ margin: 'calc(-1 * var(--spacing-05)) calc(-1 * var(--spacing-05)) 0', background: 'color-mix(in srgb, var(--color-error) 8%, white)', padding: '22px 24px 16px', borderBottom: '1px solid color-mix(in srgb, var(--color-error) 25%, white)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--shell-surface)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--status-danger-600)', marginBottom: 4 }}>스케줄 삭제</div>
                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--status-danger-600)', lineHeight: 1.6 }}>
                  <strong>"{confirmDeleteSite.siteName}"</strong> 현장의 스케줄을 삭제합니다.<br />
                  삭제된 데이터는 복구할 수 없습니다.
                  {deleteNotice && <><br />{deleteNotice}</>}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  )
}
